import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import { Scissors, Plus, ScanLine, AlertTriangle } from 'lucide-react';
import nurseryMaterialService from '../../services/nurseryMaterial';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../utils/timezone';
import { formatQuantity, materialSource, parseMaterialScan } from '../../utils/nurseryMaterial';
import type { LicensedCultivar } from '../../types/nursery';
import {
  MATERIAL_KINDS, MATERIAL_KIND_LABELS, MATERIAL_STATUS_LABELS, UNITS_FOR_KIND,
} from '../../types/nurseryMaterial';
import type { MaterialBundle, MaterialKind, SourcingRequest } from '../../types/nurseryMaterial';

/**
 * /nursery/material — propagation material on hand (seed, cuttings, budwood,
 * graftwood, explants), oldest first because that is the order it is drawn
 * in, and the requests for material nobody has (propagation plan, Phase 1).
 *
 * Recording and counting material is field work; saying where to find it,
 * or cancelling a request, is a manager's.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
type Tab = 'on-hand' | 'requests';

function BundleCard({ m, today, orgTz }: { m: MaterialBundle; today: string; orgTz: string }) {
  const pastUseBy = m.status === 'open' && m.use_by != null && m.use_by.slice(0, 10) < today;
  return (
    <Link to={`/nursery/material/${m.id}`}
      className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-neutral-500">{MATERIAL_KIND_LABELS[m.kind]} #{m.id}</p>
          <p className="text-sm font-semibold text-neutral-800 break-words">{m.cultivar_name}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-neutral-800">{formatQuantity(m.remaining, m.unit)}</p>
          <p className="text-xs text-neutral-500">
            {m.status === 'open' ? `of ${formatQuantity(m.quantity)}${m.is_estimate ? ' (estimate)' : ''}` : MATERIAL_STATUS_LABELS[m.status]}
          </p>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mt-2 break-words">
        {`Collected ${formatLocalDateInTz(m.collected_on.slice(0, 10), orgTz)} from ${materialSource(m)}`}
      </p>
      {m.latest_test && (
        <p className="text-xs text-neutral-600 mt-1">
          {`Germination ${Number(m.latest_test.germination_pct)}% (tested ${formatLocalDateInTz(m.latest_test.tested_on.slice(0, 10), orgTz)})`}
        </p>
      )}
      {m.use_by && m.status === 'open' && (
        <p className={`text-xs mt-1 ${pastUseBy ? 'text-amber-700 font-medium' : 'text-neutral-500'}`}>
          {pastUseBy && <AlertTriangle className="inline h-3 w-3 mr-1" />}
          {`${pastUseBy ? 'Past its use-by' : 'Use by'} ${formatLocalDateInTz(m.use_by.slice(0, 10), orgTz)}`}
        </p>
      )}
    </Link>
  );
}

function RequestRow({ r, canManage, orgTz, onChanged }: {
  r: SourcingRequest; canManage: boolean; orgTz: string; onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'source' | 'cancel' | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!text.trim()) { setError(mode === 'source' ? 'Say where to get it' : 'Say why'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = mode === 'source'
        ? await nurseryMaterialService.markSourced(r.id, text.trim())
        : await nurseryMaterialService.cancelRequest(r.id, text.trim());
      if (!res.success) { setError(errorMessage(res, 'Could not save')); return; }
      setMode(null);
      setText('');
      onChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const collect = () => {
    const q = new URLSearchParams({ request: String(r.id), kind: r.kind, cultivar_id: String(r.cultivar_id) });
    if (r.source_note) q.set('source_text', r.source_note);
    navigate(`/nursery/material/new?${q}`);
  };

  return (
    <li className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-800 break-words">
            {`${r.cultivar_name} · ${MATERIAL_KIND_LABELS[r.kind]}`}
          </p>
          <p className="text-xs text-neutral-500">
            {[
              r.quantity_wanted != null ? `${formatQuantity(r.quantity_wanted, r.unit)} wanted` : null,
              r.needed_by ? `by ${formatLocalDateInTz(r.needed_by.slice(0, 10), orgTz)}` : null,
              r.requested_by_name ? `asked by ${r.requested_by_name}` : null,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className={`text-xs rounded-full px-2 py-0.5 ${r.status === 'sourced' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {r.status === 'sourced' ? 'Found' : 'Nobody has found it yet'}
        </span>
      </div>
      {r.notes && <p className="text-sm text-neutral-600 mt-2 break-words">{r.notes}</p>}
      {r.source_note && (
        <p className="text-sm text-neutral-800 mt-2 break-words">
          {`Get it from: ${r.source_note}`}{r.sourced_by_name ? ` (${r.sourced_by_name})` : ''}
        </p>
      )}
      {mode && (
        <div className="mt-3 space-y-2">
          <label htmlFor={`req-${r.id}-text`} className="block text-sm font-medium text-neutral-700">
            {mode === 'source' ? 'Where to get it' : 'Why it is no longer wanted'}
          </label>
          <input id={`req-${r.id}-text`} className={inputCls} maxLength={500} value={text}
            onChange={(e) => setText(e.target.value)} />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            <Button variant="outline" size="sm" onClick={() => { setMode(null); setError(null); }}>Back</Button>
          </div>
        </div>
      )}
      {!mode && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="primary" size="sm" onClick={collect}>Collected it</Button>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => { setText(r.source_note ?? ''); setMode('source'); }}>
              {r.status === 'sourced' ? 'Change where' : 'Say where to get it'}
            </Button>
          )}
          {canManage && <Button variant="outline" size="sm" onClick={() => { setText(''); setMode('cancel'); }}>Cancel request</Button>}
        </div>
      )}
    </li>
  );
}

function AskForMaterial({ cultivars, onDone, onCancel }: {
  cultivars: LicensedCultivar[]; onDone: (note: string | null) => void; onCancel: () => void;
}) {
  const [kind, setKind] = useState<MaterialKind>('budwood');
  const [cultivarId, setCultivarId] = useState<number | ''>('');
  const [qty, setQty] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (cultivarId === '') { setError('Choose the cultivar'); return; }
    const n = qty.trim() === '' ? null : Number(qty);
    if (n != null && !(n > 0)) { setError('How much is a number above 0, or blank'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryMaterialService.createRequest({
        kind, cultivar_id: Number(cultivarId), quantity_wanted: n, unit: n != null ? UNITS_FOR_KIND[kind][0] : null,
        needed_by: neededBy || null, notes: notes.trim() || null,
      });
      if (!res.success) { setError(errorMessage(res, 'Could not send the request')); return; }
      onDone(res.data.already_open ? 'That material was already asked for. The open request is below.' : null);
    } catch (e) {
      setError(errorMessage(e, 'Could not send the request'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-3">
      <h3 className="text-base font-semibold text-neutral-800">Ask for material</h3>
      <p className="text-xs text-neutral-500">The nursery managers are told, and say where to get it.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="ask-kind" className="block text-sm font-medium text-neutral-700 mb-1">What</label>
          <select id="ask-kind" className={inputCls} value={kind} onChange={(e) => setKind(e.target.value as MaterialKind)}>
            {MATERIAL_KINDS.map((k) => <option key={k} value={k}>{MATERIAL_KIND_LABELS[k]}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ask-cultivar" className="block text-sm font-medium text-neutral-700 mb-1">Of</label>
          <select id="ask-cultivar" className={inputCls} value={cultivarId}
            onChange={(e) => setCultivarId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Choose a cultivar…</option>
            {cultivars.map((c) => <option key={c.id} value={c.id}>{c.cultivar_name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ask-qty" className="block text-sm font-medium text-neutral-700 mb-1">
            {`How many ${UNITS_FOR_KIND[kind][0]} (optional)`}
          </label>
          <input id="ask-qty" className={inputCls} type="text" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
        <div>
          <label htmlFor="ask-by" className="block text-sm font-medium text-neutral-700 mb-1">Needed by (optional)</label>
          <input id="ask-by" className={inputCls} type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ask-notes" className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
          <input id="ask-notes" className={inputCls} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Ask'}</Button>
      </div>
    </div>
  );
}

function MaterialList() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const today = getTodayInTz(orgTz);
  const { hasAccess: canOperate } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'requests' ? 'requests' : 'on-hand';
  const [kind, setKind] = useState<MaterialKind | ''>('');
  const [showClosed, setShowClosed] = useState(false);
  const [bundles, setBundles] = useState<MaterialBundle[] | null>(null);
  const [requests, setRequests] = useState<SourcingRequest[]>([]);
  const [cultivars, setCultivars] = useState<LicensedCultivar[]>([]);
  const [asking, setAsking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [b, r] = await Promise.all([
        nurseryMaterialService.list({ kind: kind || undefined, status: showClosed ? 'all' : 'open' }),
        nurseryMaterialService.requests(),
      ]);
      if (b.success) setBundles(b.data);
      else setError(errorMessage(b, 'Could not load the material'));
      if (r.success) setRequests(r.data);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the material'));
    }
  }, [kind, showClosed]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    nurseryService.getLicensedCultivars().then((r) => { if (r.success) setCultivars(r.data || []); }).catch(() => { /* asking needs the list; the page still reads */ });
  }, []);

  const onScan = (raw: string) => {
    const id = parseMaterialScan(raw);
    if (id == null) { setNotice('That is not a material label. Material labels start MAT:.'); return; }
    setScanning(false);
    navigate(`/nursery/material/${id}`);
  };

  const setTab = (t: Tab) => setParams(t === 'on-hand' ? {} : { tab: t }, { replace: true });
  const tabBtn = (t: Tab, label: string) => (
    <button type="button" aria-current={tab === t ? 'page' : undefined} onClick={() => setTab(t)}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab === t ? 'bg-primary-600 text-white' : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'}`}>
      {label}
    </button>
  );

  return (
    <>
      <PageHeader
        title="Propagation material"
        subtitle="Seed, cuttings, budwood, graftwood and explants on hand"
        icon={<Scissors className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => { setNotice(null); setScanning(true); }}>
              <ScanLine className="h-4 w-4" /> Scan a label
            </Button>
            {canOperate && (
              <Button variant="primary" size="sm" onClick={() => navigate('/nursery/material/new')}>
                <Plus className="h-4 w-4" /> Add material
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {tabBtn('on-hand', 'On hand')}
          {tabBtn('requests', `Requests${requests.length ? ` (${requests.length})` : ''}`)}
        </div>
        {notice && <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">{notice}</div>}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {tab === 'on-hand' && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-full sm:w-48">
                <label htmlFor="material-kind" className="block text-xs text-neutral-500 mb-1">Kind</label>
                <select id="material-kind" className={inputCls} value={kind} onChange={(e) => setKind(e.target.value as MaterialKind | '')}>
                  <option value="">All kinds</option>
                  {MATERIAL_KINDS.map((k) => <option key={k} value={k}>{MATERIAL_KIND_LABELS[k]}</option>)}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700 pb-2">
                <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="rounded border-neutral-300" />
                Include used up and written off
              </label>
            </div>
            {bundles == null ? (
              <div className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
            ) : bundles.length === 0 ? (
              <div className="bg-white border border-dashed border-neutral-300 rounded-xl px-6 py-10 text-center">
                <p className="text-sm text-neutral-600">No material on hand.</p>
                <p className="text-xs text-neutral-400 mt-1">Add what you cut, collect or buy in, so it can be drawn oldest first.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bundles.map((m) => <BundleCard key={m.id} m={m} today={today} orgTz={orgTz} />)}
              </div>
            )}
          </>
        )}

        {tab === 'requests' && (
          <div className="space-y-3">
            {asking ? (
              <AskForMaterial cultivars={cultivars} onCancel={() => setAsking(false)}
                onDone={(note) => { setAsking(false); setNotice(note); load(); }} />
            ) : (
              canOperate && <Button variant="primary" size="sm" onClick={() => { setNotice(null); setAsking(true); }}>Ask for material</Button>
            )}
            {requests.length === 0 ? (
              <p className="text-sm text-neutral-500">Nothing is being looked for.</p>
            ) : (
              <ul className="space-y-3">
                {requests.map((r) => <RequestRow key={r.id} r={r} canManage={canManage} orgTz={orgTz} onChanged={load} />)}
              </ul>
            )}
          </div>
        )}
      </div>
      {scanning && (
        <BarcodeScanner label="Scan a material label" onScan={onScan} onClose={() => setScanning(false)} />
      )}
    </>
  );
}

export default MaterialList;
